import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import {
	Button,
	Col,
	Form,
	message,
	Row,
	Modal,
	DatePicker,
	Input,
	Divider,
	Select,
} from "antd";
import { UndoOutlined, SearchOutlined } from "@ant-design/icons";
import equal from "fast-deep-equal";
import styled from "styled-components";
import * as COMMONS from "common/common";
import * as API from "common/api";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { audiencesApi } from "api/master";
import _ from "lodash";
import {
	AUDIENCE_PARTICIPATION_STATUS,
	AUDIENCE_PARTICIPATION_STATUS_LABEL,
} from "common";

const CustomDatePicker = styled(DatePicker)`
	.ant-picker-input > input {
		text-align: center;
	}
`;

const { Option } = Select;

const DEFAULT_VALUES_FORM_SEARCH = {
	categoryId: undefined,
	occasionId: undefined,
	address: undefined,
	memberSinceMin: undefined,
	memberSinceMax: undefined,
	participationStatus: undefined,
	occurrenceStartAt: undefined,
	occurrenceEndAt: undefined,
};

const processAudienceSearchParams = (data) => {
	return _.omitBy(
		_.pick(data, [
			"categoryId",
			"occasionId",
			"isCampaign",
			"participationStatus",
			"address",
			"occurrenceStartAt",
			"occurrenceEndAt",
			"memberSinceMin",
			"memberSinceMax",
		]),
		(value) => !value,
	);
};

const AudienceModal = (props) => {
	const { publicSettings, isAudienceModalVisible, hideAudienceModal } = props;

	const isMountedRef = COMMONS.USE_IS_MOUNTED_REF();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [audienceForm] = Form.useForm();
	const [membersFilterForm] = Form.useForm();

	const [campaignQuestions, setCampaignQuestions] = useState([]);
	const [categories, setCategories] = useState([]);
	const [occasions, setOccasions] = useState([]);

	const [resultTotal, setResultTotal] = useState(0);

	const [audienceSearchedValue, setAudienceSearchedValue] = useState(undefined);

	const [modal, contextHolder] = Modal.useModal();

	useQuery(
		[API.QUERY_KEY_ADMIN_CAMPAIGN_QUESTIONS],
		() => API.ADMIN_GET_CAMPAIGN_QUESTIONS(),
		{
			enabled: isAudienceModalVisible,
			onSuccess: (response) => {
				if (isMountedRef.current) {
					setCampaignQuestions(response?.data || []);
				}
			},
			onError: (error) => {
				if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
					navigate(COMMONS.PERMISSION_ERROR_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
					message.warning({
						content: COMMONS.ERROR_SESSION_MSG,
						key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
					});
					navigate(COMMONS.ADMIN_LOGIN_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				} else {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				}
			},
		},
	);

	useQuery(
		[API.QUERY_KEY_ADMIN_CATEGORY_TEMPLATES],
		() => API.ADMIN_GET_CATEGORY_TEMPLATES(),
		{
			enabled: isAudienceModalVisible,
			onSuccess: (response) => {
				if (isMountedRef.current) {
					setCategories(response?.data || []);
				}
			},
			onError: (error) => {
				if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
					navigate(COMMONS.PERMISSION_ERROR_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
					message.warning({
						content: COMMONS.ERROR_SESSION_MSG,
						key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
					});
					navigate(COMMONS.ADMIN_LOGIN_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				} else {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				}
			},
		},
	);

	useQuery(
		[API.QUERY_KEY_ADMIN_OCCASION_TEMPLATES],
		() => API.ADMIN_GET_OCCASION_TEMPLATES(),
		{
			enabled: isAudienceModalVisible,
			onSuccess: (response) => {
				if (isMountedRef.current) {
					setOccasions(response?.data || []);
				}
			},
			onError: (error) => {
				if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
					navigate(COMMONS.PERMISSION_ERROR_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
					message.warning({
						content: COMMONS.ERROR_SESSION_MSG,
						key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
					});
					navigate(COMMONS.ADMIN_LOGIN_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				} else {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				}
			},
		},
	);

	const audienceCreateMutation = useMutation({
		mutationFn: async (data) => {
			await audiencesApi.createDefaultAudience(data);
			message.success(COMMONS.SUCCESS_CREATE_MSG);
			hideAudienceModal();
			queryClient.invalidateQueries({
				queryKey: [API.QUERY_KEY_ADMIN_AUDIENCES],
			});
		},
	});

	const searchAudienceMembersMutation = useMutation(
		(data) => audiencesApi.searchAudienceEvents(data),
		{
			onSuccess: (response) => {
				if (isMountedRef.current) {
					setResultTotal(response?.length || 0);
				}
			},
			onError: (error) => {
				if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
					navigate(COMMONS.PERMISSION_ERROR_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
					message.warning({
						content: COMMONS.ERROR_SESSION_MSG,
						key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
					});
					navigate(COMMONS.ADMIN_LOGIN_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				} else {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				}
			},
		},
	);

	const handleCreate = () => {
		if (resultTotal > 0) {
			membersFilterForm
				.validateFields()
				.then((filterValues) => {
					if (equal(filterValues, audienceSearchedValue)) {
						modal.confirm({
							title: "オーディエンス作成",
							content: (
								<>
									<Form
										form={audienceForm}
										name="audienceForm"
										layout="vertical"
										preserve={false}
										initialValues={{
											description: undefined,
										}}
										scrollToFirstError
									>
										<Row justify="center">
											<Col span={24}>
												<Form.Item
													name="description"
													label="オーディエンス名"
													rules={[
														{
															required: true,
															whitespace: true,
															message: "オーディエンス名を入力してください",
														},
														{
															max: 50,
															message:
																"オーディエンス名は50文字以内である必要があります",
														},
													]}
												>
													<Input allowClear maxLength={50} />
												</Form.Item>
											</Col>
										</Row>
									</Form>
								</>
							),
							okText: "確認",
							okType: "primary",
							cancelText: "閉じる",
							centered: true,
							onOk(close) {
								audienceForm
									.validateFields()
									.then((audienceValues) => {
										const paramData = {
											description: audienceValues.description,
											type: COMMONS.AUDIENCE_TYPE.DEFAULT,
											searchCondition:
												processAudienceSearchParams(filterValues),
											audiences: searchAudienceMembersMutation.data,
										};

										audienceCreateMutation.mutate(paramData, {
											onSuccess: (data, variables, context) => {
												close();
											},
										});
									})
									.catch((error) => {});
							},
						});
					} else {
						message.warning(COMMONS.WARN_AUDIENCE_NOT_MATCH_MSG);
					}
				})
				.catch((error) => {});
		} else {
			message.warning(COMMONS.WARN_AUDIENCE_COUNT_ZERO_MSG);
		}
	};

	const handleFilter = () => {
		membersFilterForm
			.validateFields()
			.then((values) => {
				if (!equal(values, audienceSearchedValue)) {
					const paramData = processAudienceSearchParams(values);

					setAudienceSearchedValue(values);

					searchAudienceMembersMutation.mutate(paramData);
				}
			})
			.catch((error) => {});
	};

	const clearFilters = () => {
		membersFilterForm.resetFields();
	};

	useEffect(() => {
		if (isAudienceModalVisible) {
			clearFilters();
			setResultTotal(0);
			setAudienceSearchedValue(undefined);
		}

		//eslint-disable-next-line
	}, [isAudienceModalVisible]);

	return (
		<>
			<Modal
				title="新規作成"
				open={isAudienceModalVisible}
				onCancel={hideAudienceModal}
				footer={null}
				destroyOnClose
				centered
				width={720}
				styles={{
					body: {
						maxHeight: "90vh",
						overflowY: "auto",
						overflowX: "hidden",
					},
				}}
			>
				<Row>
					<Col xs={24} className="mb-4">
						<div className="flex justify-end">
							<Button
								type="dashed"
								icon={<UndoOutlined />}
								onClick={clearFilters}
							>
								フィルタークリア
							</Button>
						</div>
					</Col>
					<Col xs={24} className="mb-4 p-4 bg-gray-100">
						<Form
							form={membersFilterForm}
							layout="vertical"
							onFinish={handleFilter}
							initialValues={DEFAULT_VALUES_FORM_SEARCH}
						>
							<Divider>{`${COMMONS.DEFAULT_SYSTEM_TYPE}と${COMMONS.DEFAULT_SYSTEM_TYPE}プログラムで検索`}</Divider>
							<Row gutter={[16, 16]} justify="start">
								<Col xs={{ span: 24 }} md={{ span: 12 }}>
									<Form.Item
										help={COMMONS.DEFAULT_SYSTEM_TYPE}
										name="categoryId"
										className="mb-4"
									>
										<Select
											allowClear
											showSearch
											placeholder={`${COMMONS.DEFAULT_SYSTEM_TYPE}で検索`}
										>
											{categories.map((c) => (
												<Option key={c.categoryId} value={c.categoryId}>
													{c.title}
												</Option>
											))}
										</Select>
									</Form.Item>
								</Col>
								<Col xs={{ span: 24 }} md={{ span: 12 }}>
									<Form.Item
										help={`${COMMONS.DEFAULT_SYSTEM_TYPE}プログラム`}
										name="occasionId"
										className="mb-4"
									>
										<Select
											allowClear
											showSearch
											placeholder={`${COMMONS.DEFAULT_SYSTEM_TYPE}プログラムで検索`}
										>
											{occasions.map((o) => (
												<Option key={o.occasionId} value={o.occasionId}>
													{o.title}
												</Option>
											))}
										</Select>
									</Form.Item>
								</Col>
							</Row>
							<Divider>参加状況で検索</Divider>
							<Row gutter={[16, 16]} justify="start">
								<Col xs={{ span: 24 }}>
									<Form.Item name="participationStatus" className="mb-4">
										<Select
											placeholder="参加状況"
											allowClear
											options={[
												{
													value: AUDIENCE_PARTICIPATION_STATUS.PARTICIPATE,
													label:
														AUDIENCE_PARTICIPATION_STATUS_LABEL[
															AUDIENCE_PARTICIPATION_STATUS.PARTICIPATE
														],
												},
												{
													value: AUDIENCE_PARTICIPATION_STATUS.RESERVE,
													label:
														AUDIENCE_PARTICIPATION_STATUS_LABEL[
															AUDIENCE_PARTICIPATION_STATUS.RESERVE
														],
												},
												{
													value: AUDIENCE_PARTICIPATION_STATUS.CANCEL,
													label:
														AUDIENCE_PARTICIPATION_STATUS_LABEL[
															AUDIENCE_PARTICIPATION_STATUS.CANCEL
														],
												},
											]}
										/>
									</Form.Item>
								</Col>
							</Row>
							<Divider>イベント日時で検索</Divider>
							<Row gutter={[16, 16]} justify="start">
								<Col xs={{ span: 24 }}>
									<Form.Item className="mb-0">
										<Form.Item
											help="この日付から"
											name="occurrenceStartAt"
											className="inline-block mr-2 mb-4"
											style={{ width: "calc(50% - 0.25rem)" }}
											getValueFromEvent={(value) =>
												value?.startOf("minute")?.format("YYYY-MM-DD HH:mm:ss")
											}
											getValueProps={(value) => ({
												value: value ? dayjs(value) : undefined,
											})}
										>
											<CustomDatePicker
												placeholder="日付で検索"
												className="w-full"
												inputReadOnly
												showTime={{
													format: "HH:mm",
												}}
												format="YYYY-MM-DD HH:mm"
											/>
										</Form.Item>
										<Form.Item
											help="この日付まで"
											name="occurrenceEndAt"
											className="inline-block mb-4"
											style={{ width: "calc(50% - 0.25rem)" }}
											getValueFromEvent={(value) =>
												value?.endOf("minute")?.format("YYYY-MM-DD HH:mm:ss")
											}
											getValueProps={(value) => ({
												value: value ? dayjs(value) : undefined,
											})}
										>
											<CustomDatePicker
												placeholder="日付で検索"
												className="w-full"
												inputReadOnly
												showTime={{
													format: "HH:mm",
												}}
												format="YYYY-MM-DD HH:mm"
											/>
										</Form.Item>
									</Form.Item>
								</Col>
							</Row>
							<Divider>住所で検索</Divider>
							<Row gutter={[16, 16]} justify="start">
								<Col xs={{ span: 24 }}>
									<Form.Item name="address" className="mb-4">
										<Input placeholder="住所を検索" allowClear />
									</Form.Item>
								</Col>
							</Row>
							<Divider>会員登録日で検索</Divider>
							<Row gutter={[16, 16]} justify="start">
								<Col xs={{ span: 24 }}>
									<Form.Item className="mb-0">
										<Form.Item
											help="この日付から"
											name="memberSinceMin"
											className="inline-block mr-2 mb-4"
											style={{ width: "calc(50% - 0.25rem)" }}
											getValueFromEvent={(value) => value?.format("YYYY-MM-DD")}
											getValueProps={(value) => ({
												value: value ? dayjs(value) : undefined,
											})}
										>
											<CustomDatePicker
												placeholder="日付で検索"
												className="w-full"
												inputReadOnly
											/>
										</Form.Item>
										<Form.Item
											help="この日付まで"
											name="memberSinceMax"
											className="inline-block mb-4"
											style={{ width: "calc(50% - 0.25rem)" }}
											getValueFromEvent={(value) => value?.format("YYYY-MM-DD")}
											getValueProps={(value) => ({
												value: value ? dayjs(value) : undefined,
											})}
										>
											<CustomDatePicker
												placeholder="日付で検索"
												className="w-full"
												inputReadOnly
											/>
										</Form.Item>
									</Form.Item>
								</Col>
							</Row>
							<Divider />
							<Row gutter={[8, 8]} justify="center">
								<Col>
									<Form.Item
										shouldUpdate={(prevValues, currValue) =>
											!_.isEqual(prevValues, currValue)
										}
									>
										{({ getFieldsValue }) => {
											const values = getFieldsValue();
											const disabled = _.isEmpty(
												processAudienceSearchParams(values),
											);
											return (
												<Button
													type="dashed"
													icon={<SearchOutlined />}
													size="large"
													className="bg-white px-6"
													onClick={handleFilter}
													disabled={disabled}
												>
													検索
												</Button>
											);
										}}
									</Form.Item>
								</Col>
							</Row>
						</Form>
					</Col>
					<Col span={24}>
						<div
							className="p-4 rounded-full border"
							style={{
								backgroundColor: publicSettings?.PRIMARY_LIGHT_COLOR,
								borderColor: publicSettings?.PRIMARY_COLOR,
								color: publicSettings?.PRIMARY_COLOR,
							}}
						>
							<p className="text-center text-lg font-bold">
								検索結果：{resultTotal}件
							</p>
						</div>
					</Col>
					<Divider />
					<Col span={24} className="text-center">
						<Button
							type="primary"
							size="large"
							htmlType="submit"
							className="px-12"
							onClick={handleCreate}
						>
							作成
						</Button>
					</Col>
				</Row>
			</Modal>
			{contextHolder}
		</>
	);
};

export default AudienceModal;
